import React, { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
    getActivityTermsConditions,
    createActivityTermsConditions,
    updateActivityTermsConditions
} from "../activityService";
import Toster from "../../../../common/Toster";
import { abEditorWrap } from "../activityBuilderUi";


const ActivityTerms = ({ activityId, registerSave, onValidityChange }) => {
    const isBuilderMode = Boolean(registerSave);
    const queryClient = useQueryClient();
    const [terms, setTerms] = useState("");
    const [existingId, setExistingId] = useState(null);
    const [showSuccess, setShowSuccess] = useState(false);
    const [showError, setShowError] = useState(false);
    const [error, setError] = useState("");
    const [successMessage, setSuccessMessage] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Fetch Terms using React Query
    const { data: termsData, isLoading: isFetching } = useQuery({
        queryKey: ["activityTerms", activityId],
        queryFn: () => getActivityTermsConditions(activityId),
        enabled: !!activityId,
    });

    // Save/Update mutation
    const saveMutation = useMutation({
        mutationFn: async (content) => {
            if (existingId) {
                return updateActivityTermsConditions(existingId, {
                    activity_id: activityId,
                    terms_and_conditions: content
                });
            } else {
                return createActivityTermsConditions({
                    activity_id: activityId,
                    terms_and_conditions: content
                });
            }
        },
        onSuccess: () => {
            queryClient.invalidateQueries(["activityTerms", activityId]);
            if (!isBuilderMode) {
                setShowSuccess(true);
                setSuccessMessage("Terms and conditions saved successfully");
            }
        },
        onError: (error) => {
            if (isBuilderMode) return;
            setShowError(true);
            setError(error.message || "Failed to save terms and conditions");
        }
    });

    // Initialize terms when data is loaded
    useEffect(() => {
        const raw = termsData?.data;
        const list = Array.isArray(raw?.terms_and_conditions)
            ? raw.terms_and_conditions
            : Array.isArray(raw)
                ? raw
                : raw
                    ? [raw]
                    : [];

        const first = list[0];
        if (first) {
            setTerms(first.terms_and_conditions || "");
            setExistingId(first.id || null);
        } else {
            setTerms("");
            setExistingId(null);
        }
    }, [termsData]);

    useEffect(() => {
        onValidityChange?.(Boolean(String(terms || "").trim()));
    }, [terms, onValidityChange]);

    const handleSave = async () => {
        if (!terms.trim()) {
            const validationMessage = "Please enter the terms and conditions.";
            if (!isBuilderMode) {
                setError(validationMessage);
                setShowError(true);
            }
            throw new Error(validationMessage);
        }

        setIsSubmitting(true);
        try {
            await saveMutation.mutateAsync(terms);
        } finally {
            setIsSubmitting(false);
        }
    };

    useEffect(() => {
        registerSave?.(() => handleSave());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [registerSave, terms, existingId, activityId]);

    return (
        <div className="relative">
            {showError && error && (
                <Toster message={error} type="error" onClose={() => setShowError(false)} />
            )}
            {showSuccess && successMessage && (
                <Toster message={successMessage} type="success" onClose={() => setShowSuccess(false)} />
            )}

            <div className="space-y-4">
                <label className="mb-1.5 block text-sm font-medium text-gray-700">
                    Policy description <span className="text-red-500">*</span>
                </label>
                <div className={abEditorWrap}>
                    <textarea
                        value={terms}
                        onChange={(e) => setTerms(e.target.value)}
                        rows={12}
                        className="package-field-text w-full resize-y border-0 px-4 py-3 text-sm font-medium text-gray-800 focus:outline-none"
                        placeholder="Detail all terms, conditions, and participation requirements here..."
                    />
                </div>
            </div>

            <div className="mt-8 flex justify-end gap-3 sticky bottom-0 bg-white/80 backdrop-blur-sm py-4">
                {/* Terms are saved via footer "Save & Complete" */}
            </div>
        </div>
    );
};

export default ActivityTerms;
